import { useEffect, useState, type FormEvent } from 'react';
import type { Folder } from '../types';
import { flattenFolders } from '../utils';
import { Modal } from './Modal';

interface NameDialogProps {
  open: boolean;
  title: string;
  description: string;
  label: string;
  initialValue?: string;
  submitLabel: string;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (value: string) => void;
}

export function NameDialog({ open, title, description, label, initialValue = '', submitLabel, busy, error, onClose, onSubmit }: NameDialogProps) {
  const [value, setValue] = useState(initialValue);
  useEffect(() => { if (open) setValue(initialValue); }, [open, initialValue]);
  function submit(event: FormEvent) { event.preventDefault(); if (value.trim()) onSubmit(value.trim()); }
  return <Modal open={open} title={title} description={description} onClose={onClose} width="small" footer={<><button className="button button--secondary" type="button" onClick={onClose} disabled={busy}>Cancel</button><button className="button button--primary" type="submit" form="name-dialog-form" disabled={busy || !value.trim()}>{busy ? 'Saving…' : submitLabel}</button></>}>
    <form id="name-dialog-form" className="dialog-form" onSubmit={submit}>{error && <p className="form-alert" role="alert">{error}</p>}<label>{label}<input value={value} onChange={(event) => setValue(event.target.value)} maxLength={255} autoFocus /></label></form>
  </Modal>;
}

interface MoveDialogProps {
  itemKind?: 'video' | 'image';
  open: boolean;
  videoTitle: string;
  folders: Folder[];
  currentFolderId: string | null;
  busy: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (folderId: string | null) => void;
}

export function MoveDialog({ open, videoTitle, folders, currentFolderId, busy, error, onClose, onSubmit, itemKind = 'video' }: MoveDialogProps) {
  const [value, setValue] = useState(currentFolderId ?? '');
  useEffect(() => { if (open) setValue(currentFolderId ?? ''); }, [open, currentFolderId]);
  return <Modal open={open} title={`Move ${itemKind}`} description={`Choose a new folder for “${videoTitle}”.`} onClose={onClose} width="small" footer={<><button className="button button--secondary" type="button" onClick={onClose} disabled={busy}>Cancel</button><button className="button button--primary" type="button" onClick={() => onSubmit(value || null)} disabled={busy}>{busy ? 'Moving…' : `Move ${itemKind}`}</button></>}>
    <div className="dialog-form">{error && <p className="form-alert" role="alert">{error}</p>}<label>Destination<select className="control-select" value={value} onChange={(event) => setValue(event.target.value)}><option value="">Library root</option>{flattenFolders(folders).map((folder) => <option value={folder.id} key={folder.id}>{'— '.repeat(folder.depth)}{folder.name}</option>)}</select></label></div>
  </Modal>;
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description: string;
  confirmLabel: string;
  busy: boolean;
  error: string;
  recursiveOption?: boolean;
  onClose: () => void;
  onConfirm: (recursive: boolean) => void;
}

interface FolderMoveDialogProps {
  open: boolean;
  folder: Folder | null;
  folders: Folder[];
  busy: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (parentId: string | null) => void;
}

export function FolderMoveDialog({ open, folder, folders, busy, error, onClose, onSubmit }: FolderMoveDialogProps) {
  const [value, setValue] = useState(folder?.parentId ?? '');
  useEffect(() => { if (open) setValue(folder?.parentId ?? ''); }, [open, folder]);
  const descendants = new Set<string>();
  const collect = (items: Folder[]) => items.forEach((item) => { descendants.add(item.id); collect(item.children); });
  if (folder) collect(folder.children);
  descendants.add(folder?.id ?? '');
  return <Modal open={open} title="Move folder" description={folder ? `Choose a new parent for “${folder.name}”.` : ''} onClose={onClose} width="small" footer={<><button className="button button--secondary" type="button" onClick={onClose} disabled={busy}>Cancel</button><button className="button button--primary" type="button" onClick={() => onSubmit(value || null)} disabled={busy || !folder}>{busy ? 'Moving…' : 'Move folder'}</button></>}>
    <div className="dialog-form">{error && <p className="form-alert" role="alert">{error}</p>}<label>Parent folder<select className="control-select" value={value} onChange={(event) => setValue(event.target.value)} autoFocus><option value="">Library root</option>{flattenFolders(folders).filter((candidate) => !descendants.has(candidate.id)).map((candidate) => <option value={candidate.id} key={candidate.id}>{'— '.repeat(candidate.depth)}{candidate.name}</option>)}</select></label></div>
  </Modal>;
}

export function ConfirmDialog({ open, title, description, confirmLabel, busy, error, recursiveOption, onClose, onConfirm }: ConfirmDialogProps) {
  const [recursive, setRecursive] = useState(false);
  useEffect(() => { if (open) setRecursive(false); }, [open]);
  return <Modal open={open} title={title} description={description} onClose={onClose} width="small" footer={<><button className="button button--secondary" type="button" onClick={onClose} disabled={busy}>Cancel</button><button className="button button--danger" type="button" onClick={() => onConfirm(recursive)} disabled={busy}>{busy ? 'Deleting…' : confirmLabel}</button></>}>
    {error && <p className="form-alert" role="alert">{error}</p>}{recursiveOption && <label className="checkbox-field"><input className="control-choice" type="checkbox" checked={recursive} onChange={(event) => setRecursive(event.target.checked)} /><span><strong>Delete everything inside this folder</strong><small>Includes all images, videos, and nested folders, even items hidden by filters. This cannot be undone.</small></span></label>}
  </Modal>;
}
